import { LinearGradient } from 'expo-linear-gradient';
import { useEffect, useRef } from 'react';
import { Animated, Image, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  purple600: '#7C3AED',
  white: '#FFFFFF',
};

interface SplashScreenProps {
  onFinish: () => void;
}

export default function SplashScreen({ onFinish }: SplashScreenProps) {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;
  const contentFade = useRef(new Animated.Value(0)).current;
  const textFade = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const loaderRotation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Animation d'entrée du logo (immédiat)
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        friction: 6,
        tension: 40,
        useNativeDriver: true,
      }),
    ]).start();

    // Animation du texte/spinner avec délai (apparaît après)
    Animated.timing(textFade, {
      toValue: 1,
      duration: 500,
      delay: 300,
      useNativeDriver: true,
    }).start();

    Animated.timing(contentFade, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();

    // Pulse animation pour le logo
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.05,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ])
    );
    pulse.start();

    // Rotation du loader
    const rotation = Animated.loop(
      Animated.timing(loaderRotation, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      })
    );
    rotation.start();

    // Après 3 secondes, fade out
    const timer = setTimeout(() => {
      pulse.stop();
      rotation.stop();
      Animated.parallel([
        Animated.timing(contentFade, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(textFade, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start(() => {
        onFinish();
      });
    }, 3000);

    return () => {
      clearTimeout(timer);
      pulse.stop();
      rotation.stop();
    };
  }, []);

  const spin = loaderRotation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  return (
    <LinearGradient
      colors={[colors.indigo500, colors.indigo600, colors.indigo700]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.container}
    >
      <Animated.View
        style={[
          styles.centerContent,
          {
            opacity: contentFade,
            transform: [{ scale: scaleAnim }],
          },
        ]}
      >
        <Animated.View
          style={[
            styles.logoCircle,
            { transform: [{ scale: pulseAnim }] },
          ]}
        >
          <Image
            source={require('../assets/images/ernest.png')}
            style={styles.logoImage}
          />
        </Animated.View>

        <Animated.View
          style={[
            styles.textContent,
            { opacity: textFade },
          ]}
        >
          <Text style={styles.title}>Ernest</Text>
          <Text style={styles.subtitle}>Ton coach IA pour réussir</Text>

          <View style={styles.loaderContainer}>
            <Animated.View
              style={[
                styles.loader,
                { transform: [{ rotate: spin }] },
              ]}
            />
          </View>
        </Animated.View>
      </Animated.View>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  centerContent: {
    alignItems: 'center',
  },
  logoCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#FFFFFF',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    overflow: 'visible',
  },
  logoImage: {
    width: 90,
    height: 90,
    resizeMode: 'contain',
  },
  textContent: {
    alignItems: 'center',
    marginTop: 32,
  },
  title: {
    fontSize: 42,
    fontWeight: '800',
    color: colors.white,
    marginBottom: 8,
    letterSpacing: -1,
  },
  subtitle: {
    fontSize: 18,
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: '500',
  },
  loaderContainer: {
    marginTop: 48,
  },
  loader: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 3,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    borderTopColor: colors.white,
  },
});